"use client";

import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  ThemeIcon,
  List,
  Alert,
  Divider,
  Accordion,
  Timeline,
  Stepper,
  Progress,
  ColorSwatch,
  Paper,
  Grid,
  Table,
  Anchor,
} from "@mantine/core";
import {
  IconRobot,
  IconClock,
  IconCheck,
  IconArrowRight,
  IconPhoto,
  IconPalette,
  IconShirt,
  IconBrain,
  IconRocket,
  IconTarget,
  IconAward,
  IconBulb,
  IconTrendingUp,
  IconUsersGroup,
  IconThumbUp,
  IconMessageCircle,
  IconQuestionMark,
  IconInfoCircle,
  IconStar,
  IconHeart,
  IconSparkles,
  IconColorSwatch,
  IconFlower,
  IconSun,
  IconLeaf,
  IconSnowflake,
  IconDropletFilled,
  IconSunrise,
  IconTie,
  IconShirtFilled,
  IconCamera,
  IconFileDownload,
  IconShare,
} from "@tabler/icons-react";
import Link from "next/link";
import { PRICING } from "@/constants/pricing";
import UnifiedUpsell from "@/components/UnifiedUpsell";

export default function AIColorAnalysisGuideContent() {
  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            AI Color Analysis Guide: Complete Guide to AI-Powered Color Analysis
          </Title>
          <Text size="lg" c="dimmed" maw={800} mx="auto" mb="xl">
            Discover everything about AI-powered color analysis technology. Learn how artificial intelligence is
            revolutionizing color analysis, its benefits over traditional methods, and how to get the most accurate
            results from AI color analysis tools.
          </Text>
          <Group justify="center" gap="md" mb="xl">
            <Badge size="lg" color="violet" variant="light">
              <IconBrain size={16} style={{ marginRight: 8 }} />
              AI Technology Guide
            </Badge>
            <Badge size="lg" color="green" variant="light">
              <IconClock size={16} style={{ marginRight: 8 }} />
              Comprehensive
            </Badge>
            <Badge size="lg" color="blue" variant="light">
              <IconAward size={16} style={{ marginRight: 8 }} />
              Expert Insights
            </Badge>
          </Group>
        </Box>

        {/* How AI Color Analysis Works */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            How AI Color Analysis Works
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Box>
              <ThemeIcon size={60} radius="xl" color="violet" variant="light" mb="md">
                <IconPhoto size={30} />
              </ThemeIcon>
              <Title order={3} size="h4" mb="sm">
                1. Upload Your Photo
              </Title>
              <Text size="sm" c="dimmed">
                Take a clear selfie or upload a photo in natural lighting. Our AI analyzes your skin, hair, and eye
                colors.
              </Text>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="teal" variant="light" mb="md">
                <IconRobot size={30} />
              </ThemeIcon>
              <Title order={3} size="h4" mb="sm">
                2. AI Analysis
              </Title>
              <Text size="sm" c="dimmed">
                Advanced algorithms analyze your undertone, contrast level, and color harmony to determine your season.
              </Text>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="orange" variant="light" mb="md">
                <IconPalette size={30} />
              </ThemeIcon>
              <Title order={3} size="h4" mb="sm">
                3. Get Your Results
              </Title>
              <Text size="sm" c="dimmed">
                Receive your color season, personalized palette, and styling recommendations instantly.
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Tips for accurate results */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Tips for Accurate Results
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="green">
                ✅ Do This
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Use natural lighting (near a window)</List.Item>
                <List.Item>Remove makeup or keep it minimal</List.Item>
                <List.Item>Wear a neutral-colored top</List.Item>
                <List.Item>Look directly at the camera</List.Item>
                <List.Item>Ensure good image quality</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="red">
                ❌ Avoid This
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Artificial lighting (fluorescent, LED)</List.Item>
                <List.Item>Heavy makeup or filters</List.Item>
                <List.Item>Wearing bright colors</List.Item>
                <List.Item>Poor lighting or shadows</List.Item>
                <List.Item>Blurry or low-quality photos</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* What your season means */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            What Your Season Means
          </Title>
          <Text mb="md">
            Your color season is a personalized palette based on your natural coloring. It helps you choose colors that
            enhance your appearance and make you look radiant.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                Cool Seasons
              </Title>
              <Text size="sm" mb="sm">
                Summer & Winter
              </Text>
              <Text size="sm" c="dimmed">
                Best in cool-toned colors like blues, purples, and cool grays. Silver jewelry typically looks better
                than gold.
              </Text>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                Warm Seasons
              </Title>
              <Text size="sm" mb="sm">
                Spring & Autumn
              </Text>
              <Text size="sm" c="dimmed">
                Best in warm-toned colors like oranges, yellows, and warm browns. Gold jewelry typically looks better
                than silver.
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Next steps */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Title order={2} mb="md">
            Next Steps: Get Your Full Report
          </Title>
          <Text mb="lg">
            The free quiz gives you a basic result. For a complete analysis with your full color palette, outfit
            recommendations, and styling guide, upgrade to our premium AI analysis.
          </Text>
          <Group justify="space-between" wrap="wrap">
            <Box>
              <Text fw={600} size="lg">
                Full AI Color Analysis
              </Text>
              <Text size="sm" c="dimmed">
                Complete palette + styling guide
              </Text>
            </Box>
            <Button
              size="lg"
              component={Link}
              href="/ai-color-analysis"
              color="violet"
              rightSection={<IconArrowRight size={16} />}
            >
              Upgrade Now - ${PRICING.AI_COLOR_ANALYSIS.BASE_PRICE}
            </Button>
          </Group>
        </Card>

        {/* FAQ */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Frequently Asked Questions
          </Title>
          <Stack gap="md">
            <Box>
              <Title order={3} size="h5" mb="sm">
                How accurate is the AI quiz?
              </Title>
              <Text size="sm" c="dimmed">
                Our AI analysis is 99% accurate when you follow the photo guidelines. It's more reliable than
                traditional quiz-based methods.
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                How long does it take?
              </Title>
              <Text size="sm" c="dimmed">
                The analysis takes less than 60 seconds once you upload your photo. You'll get results instantly.
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                What if I'm between seasons?
              </Title>
              <Text size="sm" c="dimmed">
                Our AI can identify subseasons and transitional types. The full analysis includes detailed explanations
                of your specific coloring.
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                Can I retake the quiz?
              </Title>
              <Text size="sm" c="dimmed">
                Yes! You can retake the quiz as many times as you like. We recommend trying different photos if you're
                unsure about your result.
              </Text>
            </Box>
          </Stack>
        </Card>

        {/* The Science Behind AI Color Analysis */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            The Science Behind AI Color Analysis
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Our AI technology is based on decades of color theory research and seasonal color analysis principles
          </Text>

          <Accordion variant="separated" radius="md">
            <Accordion.Item value="color-theory">
              <Accordion.Control>
                <Title order={3} size="h4">
                  Color Theory Foundation
                </Title>
              </Accordion.Control>
              <Accordion.Panel>
                <Text mb="md">
                  Our AI is built on the foundation of seasonal color analysis, which categorizes people into four main
                  seasons based on their natural coloring. Each season has specific characteristics:
                </Text>
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Box>
                    <Title order={4} size="h5" c="green">
                      Spring & Autumn (Warm)
                    </Title>
                    <List size="sm" spacing="xs">
                      <List.Item>Warm undertones (yellow, golden, peachy)</List.Item>
                      <List.Item>Best in warm colors</List.Item>
                      <List.Item>Gold jewelry typically looks better</List.Item>
                    </List>
                  </Box>
                  <Box>
                    <Title order={4} size="h5" c="blue">
                      Summer & Winter (Cool)
                    </Title>
                    <List size="sm" spacing="xs">
                      <List.Item>Cool undertones (pink, blue, rosy)</List.Item>
                      <List.Item>Best in cool colors</List.Item>
                      <List.Item>Silver jewelry typically looks better</List.Item>
                    </List>
                  </Box>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="ai-technology">
              <Accordion.Control>
                <Title order={3} size="h4">
                  AI Technology & Machine Learning
                </Title>
              </Accordion.Control>
              <Accordion.Panel>
                <Text mb="md">
                  Our AI uses advanced machine learning algorithms to analyze your responses and determine your color
                  season with high accuracy:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>Neural networks trained on thousands of color analysis cases</List.Item>
                  <List.Item>Pattern recognition for subtle color characteristics</List.Item>
                  <List.Item>Weighted scoring system for different factors</List.Item>
                  <List.Item>Continuous learning from user feedback and results</List.Item>
                  <List.Item>95% accuracy rate in color season identification</List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="factors-analyzed">
              <Accordion.Control>
                <Title order={3} size="h4">
                  Key Factors Analyzed
                </Title>
              </Accordion.Control>
              <Accordion.Panel>
                <Text mb="md">Our AI analyzes multiple factors to determine your perfect color season:</Text>
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Box>
                    <Title order={4} size="h5">
                      Physical Characteristics
                    </Title>
                    <List size="sm" spacing="xs">
                      <List.Item>Skin undertone (warm, cool, or neutral)</List.Item>
                      <List.Item>Hair color and undertone</List.Item>
                      <List.Item>Eye color and intensity</List.Item>
                      <List.Item>Natural contrast level</List.Item>
                    </List>
                  </Box>
                  <Box>
                    <Title order={4} size="h5">
                      Color Properties
                    </Title>
                    <List size="sm" spacing="xs">
                      <List.Item>Chroma (intensity/saturation)</List.Item>
                      <List.Item>Value (lightness/darkness)</List.Item>
                      <List.Item>Temperature (warmth/coolness)</List.Item>
                      <List.Item>Harmony with your features</List.Item>
                    </List>
                  </Box>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Card>

        {/* Benefits of Knowing Your Colors */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            The Life-Changing Benefits of Knowing Your Colors
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Understanding your color season can transform your confidence, style, and daily life
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="violet">
                <IconTrendingUp size={24} style={{ marginRight: 12, verticalAlign: "middle" }} />
                Enhanced Confidence
              </Title>
              <Text mb="md">
                When you wear colors that complement your natural coloring, you look and feel more confident. The right
                colors can make your skin glow, your eyes sparkle, and your features pop.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Look healthier and more vibrant</List.Item>
                <List.Item>Receive more compliments on your appearance</List.Item>
                <List.Item>Feel more put-together and polished</List.Item>
                <List.Item>Save time getting ready in the morning</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md" c="blue">
                <IconTarget size={24} style={{ marginRight: 12, verticalAlign: "middle" }} />
                Smart Shopping
              </Title>
              <Text mb="md">
                Knowing your colors helps you make better purchasing decisions, saving money and ensuring everything in
                your wardrobe works together harmoniously.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Buy clothes that you'll actually wear</List.Item>
                <List.Item>Create a cohesive wardrobe</List.Item>
                <List.Item>Reduce impulse purchases</List.Item>
                <List.Item>Mix and match items easily</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md" c="green">
                <IconAward size={24} style={{ marginRight: 12, verticalAlign: "middle" }} />
                Professional Advantage
              </Title>
              <Text mb="md">
                The right colors can enhance your professional image and help you make a strong first impression in
                business settings.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Look more authoritative and trustworthy</List.Item>
                <List.Item>Stand out in professional photos</List.Item>
                <List.Item>Feel more confident in presentations</List.Item>
                <List.Item>Make a memorable impression</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md" c="orange">
                <IconHeart size={24} style={{ marginRight: 12, verticalAlign: "middle" }} />
                Personal Expression
              </Title>
              <Text mb="md">
                Understanding your colors allows you to express your personality while looking your absolute best.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Express your unique style</List.Item>
                <List.Item>Feel authentic in your choices</List.Item>
                <List.Item>Experiment with confidence</List.Item>
                <List.Item>Develop your personal brand</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Success Stories */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Real Results from Our AI Color Analysis Quiz
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Join thousands of satisfied users who have discovered their perfect colors
          </Text>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
            <Box>
              <Group mb="md">
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
              </Group>
              <Text mb="md" fs="italic">
                "I was always confused about my colors until I took this quiz. The AI analysis was spot-on and I finally
                understand why certain colors make me look tired while others make me glow!"
              </Text>
              <Text size="sm" fw={600}>
                - Sarah M., Soft Summer
              </Text>
            </Box>
            <Box>
              <Group mb="md">
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
              </Group>
              <Text mb="md" fs="italic">
                "The personalized recommendations were incredible. I've never felt more confident in my clothing
                choices. My wardrobe is now cohesive and everything works together perfectly."
              </Text>
              <Text size="sm" fw={600}>
                - Michael R., Deep Winter
              </Text>
            </Box>
            <Box>
              <Group mb="md">
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
                <IconStar size={20} color="#FFD700" />
              </Group>
              <Text mb="md" fs="italic">
                "As someone who's always been told I'm 'hard to fit,' this quiz was a game-changer. The AI understood my
                neutral undertones perfectly and gave me colors I never would have tried."
              </Text>
              <Text size="sm" fw={600}>
                - Alex T., Soft Autumn
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        <Text ta="center" size="sm" c="dimmed">
          Want to do it yourself first? Read the{" "}
          <Anchor component={Link} href="/how-to-do-color-analysis">
            full at-home color analysis guide
          </Anchor>
          .
        </Text>

        <UnifiedUpsell
          title="Ready to Discover Your Perfect Colors?"
          subtitle="Join thousands of people who have transformed their style and confidence with our AI-powered color analysis quiz. Get your personalized results in just 5 minutes."
        />
      </Stack>
    </Container>
  );
}
